import { type RefObject, useEffect } from 'react';

import { useFeatureValue } from '@/hooks/useFeatureValue';
import {
  MEMBERSHIP_DASHBOARD_SELECTION_KEY,
  SIGN_UP_V2_SELECTION_KEY,
} from '@/services/content-a/fetch-general-page';
import { FeatureConfigKeys } from '@/utils/athena-feature-config';

const LIGHT_MODE_LOCK_CLASS = 'chirp-light-mode-lock';
const CMS_CONTENT_BLOCK_SELECTOR = '[class*="content-block-module_cms-content-block"]';
const LIGHT_MODE_LOCK_WORKAROUND_SELECTION_KEYS = new Set([
  MEMBERSHIP_DASHBOARD_SELECTION_KEY,
  SIGN_UP_V2_SELECTION_KEY,
]);

const removeLightModeLockFromContainer = (containerElement: HTMLElement): void => {
  const blocks = containerElement.querySelectorAll<HTMLElement>(CMS_CONTENT_BLOCK_SELECTOR);
  blocks.forEach(blockElement => {
    if (!blockElement.classList.contains(LIGHT_MODE_LOCK_CLASS)) {
      return;
    }
    blockElement.classList.remove(LIGHT_MODE_LOCK_CLASS);
  });
};

interface UseCmsLightModeLockWorkaroundProps {
  containerRef: RefObject<HTMLElement>;
  selectionKey?: string;
}

export const useCmsLightModeLockWorkaround = ({
  containerRef,
  selectionKey,
}: UseCmsLightModeLockWorkaroundProps) => {
  const isLightModeLockWorkaroundEnabled = useFeatureValue(
    FeatureConfigKeys.CMS_ZONE_LIGHT_MODE_LOCK_WORKAROUND_ENABLED,
    false,
  );

  useEffect(() => {
    const shouldRemoveLightModeLock =
      isLightModeLockWorkaroundEnabled &&
      LIGHT_MODE_LOCK_WORKAROUND_SELECTION_KEYS.has(selectionKey ?? '');

    if (!shouldRemoveLightModeLock) {
      return;
    }

    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }

    removeLightModeLockFromContainer(containerElement);

    const observer = new MutationObserver(() => {
      removeLightModeLockFromContainer(containerElement);
    });
    observer.observe(containerElement, {
      attributeFilter: ['class'],
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [containerRef, isLightModeLockWorkaroundEnabled, selectionKey]);
};
